import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

import { cn } from '@/utils/cn';
import { IconButton } from '@/components/atoms/IconButton';
import styles from './Drawer.module.scss';

const MotionIconButton = motion.create(IconButton);

const SWIPE_THRESHOLD = 80;
import type {
  DrawerProps,
  DrawerTriggerProps,
  DrawerPortalProps,
  DrawerOverlayProps,
  DrawerContentProps,
  DrawerHeaderProps,
  DrawerFooterProps,
  DrawerTitleProps,
  DrawerDescriptionProps,
  DrawerOverlayRef,
  DrawerContentRef,
} from './Drawer.types';

export const Drawer: React.FC<DrawerProps> = DialogPrimitive.Root;

export const DrawerTrigger: React.FC<DrawerTriggerProps> = DialogPrimitive.Trigger;

export const DrawerPortal: React.FC<DrawerPortalProps> = DialogPrimitive.Portal;

export const DrawerClose: React.FC<DialogPrimitive.DialogCloseProps> = DialogPrimitive.Close;

export const DrawerOverlay = React.forwardRef<DrawerOverlayRef, DrawerOverlayProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(styles.drawer__overlay, className)}
      {...props}
    />
  )
);
DrawerOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DrawerContent = React.forwardRef<DrawerContentRef, DrawerContentProps>(
  ({ className, children, side = 'right', variant, onSwipeToClose, closeSrLabel = 'Close', animateCloseButton = false, ...props }, ref) => {
    // Ref interno para manipulación directa del DOM (sin pasar por React state)
    const innerRef = React.useRef<HTMLDivElement | null>(null);
    const touchRef = React.useRef({
      startY: 0,
      isDragging: false,
      isClosing: false,
      closeTimer: null as ReturnType<typeof setTimeout> | null,
    });

    const isBottom = side === 'bottom';
    const canSwipe = isBottom && !!onSwipeToClose;

    // Fusionar el ref externo con el interno
    const mergedRef = React.useCallback(
      (el: HTMLDivElement | null) => {
        innerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref).current = el;
      },
      [ref]
    );

    React.useEffect(() => {
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const t = touchRef.current.closeTimer;
        if (t) clearTimeout(t);
      };
    }, []);

    // Teclado virtual: en drawers inferiores con inputs, el teclado taparía el
    // contenido. Elevamos el drawer su altura con la VisualViewport API. Este
    // efecto SOLO escribe la variable CSS `--drawer-keyboard-offset` que el SCSS
    // aplica al `bottom`; NO toca el `transform` ni la lógica de swipe/animación.
    React.useEffect(() => {
      if (!isBottom || typeof window === 'undefined') return;
      const vv = window.visualViewport;
      if (!vv) return;
      const update = () => {
        const el = innerRef.current;
        if (!el) return;
        const offset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        el.style.setProperty('--drawer-keyboard-offset', `${offset}px`);
      };
      update();
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
      return () => {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
        innerRef.current?.style.removeProperty('--drawer-keyboard-offset');
      };
    }, [isBottom]);

    const resetDragStyles = (el: HTMLDivElement) => {
      el.style.transform = '';
      el.style.transition = '';
      el.style.visibility = '';
    };

    const handleTouchStart = React.useCallback(
      (e: React.TouchEvent) => {
        const t = touchRef.current;
        if (!canSwipe || !e.touches[0] || t.isClosing) return;
        if (t.closeTimer) { clearTimeout(t.closeTimer); t.closeTimer = null; }
        const el = innerRef.current;
        if (el) resetDragStyles(el);
        t.startY = e.touches[0].clientY;
        t.isDragging = true;
      },
      [canSwipe]
    );

    const handleTouchMove = React.useCallback(
      (e: React.TouchEvent) => {
        const t = touchRef.current;
        if (!canSwipe || !e.touches[0] || !t.isDragging) return;
        const delta = e.touches[0].clientY - t.startY;
        const y = Math.max(0, delta);
        const el = innerRef.current;
        if (el) el.style.transform = `translateY(${y}px)`;
      },
      [canSwipe]
    );

    const handleTouchEnd = React.useCallback(() => {
      const t = touchRef.current;
      if (!canSwipe || !t.isDragging) return;
      t.isDragging = false;

      const el = innerRef.current;
      if (!el) return;

      const match = el.style.transform.match(/translateY\((\d+(?:\.\d+)?)px\)/);
      const currentY = match ? parseFloat(match[1] ?? '0') : 0;

      if (currentY >= SWIPE_THRESHOLD) {
        t.isClosing = true;
        // Aplicar transición y forzar reflow para que el browser parta del valor actual
        el.style.transition = 'transform 220ms ease-in';
        void el.offsetHeight; // force reflow
        el.style.transform = `translateY(${window.innerHeight}px)`;

        t.closeTimer = setTimeout(() => {
          t.closeTimer = null;
          t.isClosing = false;
          if (el) {
            // Ocultar antes de que Radix ejecute su animación de cierre,
            // para que el animationend dispare y el portal se desmonte correctamente
            el.style.visibility = 'hidden';
            el.style.transition = '';
            el.style.transform = '';
          }
          onSwipeToClose?.();
        }, 220);
      } else {
        // Volver a posición inicial con animación suave
        el.style.transition = 'transform 200ms ease-out';
        el.style.transform = '';
        setTimeout(() => {
          if (innerRef.current) innerRef.current.style.transition = '';
        }, 200);
      }
    }, [canSwipe, onSwipeToClose]);

    return (
      <DrawerPortal>
        <DrawerOverlay />
        <DialogPrimitive.Content
          ref={mergedRef}
          className={cn(styles.drawer__content, className)}
          data-side={side}
          data-variant={variant}
          {...props}
        >
          {canSwipe && (
            <div
              className={styles.drawer__handle}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
            >
              <div className={styles.drawer__handleBar} aria-hidden />
            </div>
          )}
          {children}
          {animateCloseButton ? (
            <DialogPrimitive.Close asChild>
              <MotionIconButton
                icon={X}
                variant="soft"
                aria-label={closeSrLabel}
                className={styles.drawer__close}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: 0.18, ease: [0.42, 0, 0.58, 1] }}
              />
            </DialogPrimitive.Close>
          ) : (
            <DialogPrimitive.Close asChild>
              <IconButton
                icon={X}
                variant="soft"
                aria-label={closeSrLabel}
                className={styles.drawer__close}
              />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DrawerPortal>
    );
  }
);
DrawerContent.displayName = DialogPrimitive.Content.displayName;

export const DrawerHeader: React.FC<DrawerHeaderProps> = ({ className, ...props }) => (
  <div className={cn(styles.drawer__header, className)} {...props} />
);
DrawerHeader.displayName = 'DrawerHeader';

export const DrawerBody: React.FC<{ className?: string; children?: React.ReactNode }> = ({
  className,
  ...props
}) => <div className={cn(styles.drawer__body, className)} {...props} />;
DrawerBody.displayName = 'DrawerBody';

export const DrawerFooter: React.FC<DrawerFooterProps> = ({ className, ...props }) => (
  <div className={cn(styles.drawer__footer, className)} {...props} />
);
DrawerFooter.displayName = 'DrawerFooter';

export const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  DrawerTitleProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(styles.drawer__title, className)}
    {...props}
  />
));
DrawerTitle.displayName = DialogPrimitive.Title.displayName;

export const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  DrawerDescriptionProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn(styles.drawer__description, className)}
    {...props}
  />
));
DrawerDescription.displayName = DialogPrimitive.Description.displayName;
